import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Castle, Skull, Package, Crown, Tent, Shield } from 'lucide-react';
import { getTilesetConfig } from '../../data/tilesets';

const DEFAULT_RENDER_TILE_SIZE = 32;

const CITY_ICONS = {
  0: { icon: Tent, color: '#8B4513', label: 'Campamento' },
  1: { icon: Castle, color: '#4682B4', label: 'Pueblo' },
  2: { icon: Shield, color: '#DAA520', label: 'Ciudad' },
  3: { icon: Crown, color: '#FFD700', label: 'Capital' },
};

const CLAN_COLORS = {
  0: '#808080',
  1: '#FF0000',
  2: '#000000',
  3: '#8B0000',
  4: '#FFD700',
};

const TERRAIN_FALLBACK_COLORS = {
  0: '#31452a',
  1: '#5b6470',
  2: '#1f6f92',
  3: '#45495a',
  4: '#154c75',
  5: '#1f5d35',
  6: '#725f3a',
  7: '#335f52',
  8: '#5d6068',
  9: '#7f6b48',
  10: '#b7a36a',
  11: '#123f68',
  12: '#65727a',
  13: '#183f28',
  14: '#3f4552'
};

function getDefaultTerrainRenderMode(terrainId) {
  const map = {
    0: 'fixed',
    1: 'autotile',
    2: 'autotile',
    3: 'autotile',
    4: 'autotile',
    5: 'autotile',
    6: 'autotile',
    7: 'autotile',
    8: 'fixed',
    9: 'fixed',
    10: 'fixed',
    11: 'fixed',
    12: 'fixed',
    13: 'fixed',
    14: 'fixed'
  };
  return map[Number(terrainId)] || null;
}

function normalizeTerrainRenderMode(terrain) {
  const canonical = getDefaultTerrainRenderMode(terrain?.terrainId);
  if (canonical) return canonical;
  if (terrain?.renderMode === 'autotile') return 'autotile';
  if (terrain?.renderMode === 'hybrid') return 'hybrid';
  if (terrain?.autotile) return 'autotile';
  if (Array.isArray(terrain?.cells) && terrain.cells.length > 0) return 'hybrid';
  return 'fixed';
}

function resolveTilesetImageSrc(image, apiBaseUrl = '') {
  if (!image) return '/assets/tiles/tiles_v2.png';
  if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('blob:')) return image;
  if (image.startsWith('/uploads/')) return `${apiBaseUrl}${image}`;
  return image;
}

const MapCanvas = ({
  sector,
  tiles = [],
  activeLayer = 0,
  selectedTile = null,
  isDrawing = false,
  onTilePaint,
  onTileErase,
  cities = [],
  enemies = [],
  objects = [],
  events = [],
  portals = [],
  editorMode = 'terrain',
  viewport = null,
  showCities = true,
  showEnemies = true,
  showObjects = true,
  showEvents = true,
  showPortals = true,
  showBotMarker = true,
  showGrid = true,
  renderSizeOverride = null,
  movementPreview = [],
  onClickTile,
  onHoverEventChange,
  testBotCoordinate = null,
  botClanId = null,
  tilesetConfig,
}) => {
  const canvasRef = useRef(null);
  const [tilesetLoaded, setTilesetLoaded] = useState(false);
  const [objectImagesVersion, setObjectImagesVersion] = useState(0);
  const [hoverCoord, setHoverCoord] = useState(null);
  const tilesetImg = useMemo(() => new Image(), []);
  const objectImageCache = useRef({});
  const apiBaseUrl = import.meta.env.VITE_API_URL || '';

  const width = sector?.width || 20;
  const height = sector?.height || 15;
  const tileset = useMemo(
    () => tilesetConfig || getTilesetConfig(sector?.tileset_id || 'overworld_v1'),
    [tilesetConfig, sector?.tileset_id]
  );
  const viewportWidth = Math.max(1, Math.min(width, viewport?.width || width));
  const viewportHeight = Math.max(1, Math.min(height, viewport?.height || height));
  const viewportMode = Boolean(viewport);
  const focusCoordinate = viewport?.centerCoordinate ?? testBotCoordinate ?? 0;
  const focusX = focusCoordinate % width;
  const focusY = Math.floor(focusCoordinate / width);
  const maxStartX = Math.max(0, width - viewportWidth);
  const maxStartY = Math.max(0, height - viewportHeight);
  const startX = viewportMode
    ? Math.min(maxStartX, Math.max(0, focusX - Math.floor(viewportWidth / 2)))
    : 0;
  const startY = viewportMode
    ? Math.min(maxStartY, Math.max(0, focusY - Math.floor(viewportHeight / 2)))
    : 0;
  const endX = viewportMode ? Math.min(width, startX + viewportWidth) : width;
  const endY = viewportMode ? Math.min(height, startY + viewportHeight) : height;
  const renderTileSize = renderSizeOverride || tileset.renderSize || DEFAULT_RENDER_TILE_SIZE;
  const sourceTileSize = tileset.tileSize || 48;
  const gameEntityInset = editorMode === 'game'
    ? Math.max(5, Math.round(renderTileSize * 0.18))
    : 2;
  const gameEntitySize = Math.max(16, renderTileSize - (gameEntityInset * 2));
  const terrainById = useMemo(() => {
    const map = new Map();
    (tileset.terrains || []).forEach((terrain) => map.set(Number(terrain.terrainId), terrain));
    return map;
  }, [tileset]);

  useEffect(() => {
    setTilesetLoaded(false);
    tilesetImg.onload = () => setTilesetLoaded(true);
    tilesetImg.onerror = () => setTilesetLoaded(true);
    tilesetImg.src = resolveTilesetImageSrc(tileset.image, apiBaseUrl);
  }, [tilesetImg, tileset.image, apiBaseUrl]);

  const getObjectImage = (filename) => {
    if (!filename) return null;
    if (objectImageCache.current[filename]) return objectImageCache.current[filename];

    const img = new Image();
    img.onload = () => setObjectImagesVersion(v => v + 1);
    img.onerror = () => setObjectImagesVersion(v => v + 1);
    img.src = `${apiBaseUrl}/uploads/gallery/${filename}`;
    objectImageCache.current[filename] = img;
    return img;
  };

  const getTileAt = (x, y, layer, currentTiles) => {
    const coordinate = y * width + x;
    return currentTiles.find(t => Number(t.coordinate) === Number(coordinate) && Number(t.layer) === Number(layer));
  };

  const resolveSourceRect = (tile, x, y, layer, currentTiles) => {
    const terrainType = tile?.terrain_type;
    const terrain = terrainById.get(Number(terrainType)) || terrainById.get(0);
    if (!terrain) return { x: 0, y: 0 };

    const forcedVariant = tile?.metadata?.terrain_variant;
    if (forcedVariant && terrainType !== undefined && terrainType !== null) {
      return {
        x: Number(forcedVariant.x || 0) * sourceTileSize,
        y: Number(forcedVariant.y || 0) * sourceTileSize
      };
    }

    const renderMode = normalizeTerrainRenderMode(terrain);
    if (renderMode === 'autotile') {
      let mask = 0;
      const addIfCompatible = (nx, ny, bit) => {
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) {
          mask += bit;
          return;
        }

        const neighborTerrainType = getTileAt(nx, ny, layer, currentTiles)?.terrain_type;
        if (Number(neighborTerrainType) === Number(terrainType)) {
          mask += bit;
          return;
        }

        const terrainGroup = terrain.maskMap || 'default';
        const neighborGroup = terrainById.get(Number(neighborTerrainType))?.maskMap || 'default';
        const compatible = {
          water: ['water', 'lake'],
          lake: ['water', 'lake'],
          beach: ['beach', 'water', 'lake'],
          road: ['road'],
          wall: ['wall'],
          forest: ['forest'],
          swamp: ['swamp', 'water']
        };

        if (compatible[terrainGroup]?.includes(neighborGroup)) mask += bit;
      };

      addIfCompatible(x, y - 1, 1);
      addIfCompatible(x + 1, y, 2);
      addIfCompatible(x, y + 1, 4);
      addIfCompatible(x - 1, y, 8);
      return { x: mask * sourceTileSize, y: Number(terrain.autotileRow || 0) * sourceTileSize };
    }

    const cells = Array.isArray(terrain.cells) ? terrain.cells : [];
    if (renderMode === 'hybrid' && cells.length > 0) {
      const variantIndex = Math.abs((x * 31) + (y * 17) + Number(terrainType || 0)) % cells.length;
      const cell = cells[variantIndex];
      return { x: Number(cell.x || 0) * sourceTileSize, y: Number(cell.y || 0) * sourceTileSize };
    }

    if (terrain.frame) {
      return { x: Number(terrain.frame.x || 0) * sourceTileSize, y: Number(terrain.frame.y || 0) * sourceTileSize };
    }

    return { x: 0, y: 0 };
  };

  const renderMap = () => {
    const canvas = canvasRef.current;
    if (!canvas || !tilesetLoaded) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const canDrawTileset = tilesetImg.complete && tilesetImg.naturalWidth > 0 && tilesetImg.naturalHeight > 0;
    const drawTerrainFallback = (x, y, terrainType) => {
      const drawX = (x - startX) * renderTileSize;
      const drawY = (y - startY) * renderTileSize;
      ctx.fillStyle = TERRAIN_FALLBACK_COLORS[Number(terrainType)] || TERRAIN_FALLBACK_COLORS[0];
      ctx.fillRect(drawX, drawY, renderTileSize, renderTileSize);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.035)';
      ctx.fillRect(drawX, drawY, renderTileSize, 1);
      ctx.fillRect(drawX, drawY, 1, renderTileSize);
    };

    try {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#0a0b1e';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (!Array.isArray(tiles) || tiles.length === 0) {
        for (let y = startY; y < endY; y++) {
          for (let x = startX; x < endX; x++) {
            drawTerrainFallback(x, y, 0);
          }
        }
      }

      [0, 1, 2].forEach(layer => {
        for (let y = startY; y < endY; y++) {
          for (let x = startX; x < endX; x++) {
            const tile = getTileAt(x, y, layer, tiles);
            if (!tile || tile.terrain_type === -1 || tile.terrain_type === undefined) continue;
            if (tile.terrain_type < 0) continue;

            const sourceRect = resolveSourceRect(tile, x, y, layer, tiles);
            if (canDrawTileset) {
              try {
                ctx.drawImage(
                  tilesetImg,
                  sourceRect.x,
                  sourceRect.y,
                  sourceTileSize,
                  sourceTileSize,
                  (x - startX) * renderTileSize,
                  (y - startY) * renderTileSize,
                  renderTileSize,
                  renderTileSize
                );
              } catch (tileError) {
                drawTerrainFallback(x, y, tile.terrain_type);
              }
            } else {
              drawTerrainFallback(x, y, tile.terrain_type);
            }
          }
        }
      });

      if (showCities) cities.forEach(city => {
        if (city.coordinate !== 0 && !city.coordinate) return;
        const x = city.coordinate % width;
        const y = Math.floor(city.coordinate / width);
        if (x < startX || x >= endX || y < startY || y >= endY) return;
        const cityType = CITY_ICONS[city.city_type] || CITY_ICONS[0];
        const cityClanId = Number(city.clan || 0);
        const activeBotClanId = Number(botClanId || 0);
        const isOwnClan = activeBotClanId > 0 && cityClanId > 0 && activeBotClanId === cityClanId;
        const clanColor = editorMode === 'game'
          ? (isOwnClan ? '#22C55E' : '#EF4444')
          : (CLAN_COLORS[city.clan] || '#808080');
        const cityImg = editorMode === 'game' ? null : (city.image ? getObjectImage(city.image) : null);
        const canDrawCityImage = cityImg && cityImg.complete && cityImg.naturalWidth > 0;
        const cityX = (x - startX) * renderTileSize + gameEntityInset;
        const cityY = (y - startY) * renderTileSize + gameEntityInset;
        const citySize = gameEntitySize;
        if (canDrawCityImage) {
          ctx.save();
          ctx.beginPath();
          if (typeof ctx.roundRect === 'function') {
            ctx.roundRect(cityX, cityY, citySize, citySize, 6);
          } else {
            ctx.rect(cityX, cityY, citySize, citySize);
          }
          ctx.clip();
          ctx.drawImage(
            cityImg,
            cityX,
            cityY,
            citySize,
            citySize
          );
          ctx.restore();
        } else {
          ctx.fillStyle = editorMode === 'game'
            ? (isOwnClan ? 'rgba(34, 197, 94, 0.28)' : 'rgba(239, 68, 68, 0.28)')
            : 'rgba(0, 0, 0, 0.55)';
          ctx.fillRect(cityX, cityY, citySize, citySize);
          ctx.fillStyle = cityType.color;
          ctx.font = 'bold 16px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(['C', 'P', 'U', 'K'][city.city_type] || 'C', cityX + citySize / 2, cityY + citySize / 2);
        }
        ctx.strokeStyle = clanColor;
        ctx.lineWidth = 2;
        ctx.strokeRect(cityX, cityY, citySize, citySize);
      });

      if (showEnemies) enemies.forEach(enemy => {
        if (enemy.coordinate !== 0 && !enemy.coordinate) return;
        const x = enemy.coordinate % width;
        const y = Math.floor(enemy.coordinate / width);
        if (x < startX || x >= endX || y < startY || y >= endY) return;
        ctx.fillStyle = 'rgba(139, 0, 0, 0.7)';
        ctx.fillRect((x - startX) * renderTileSize + 2, (y - startY) * renderTileSize + 2, renderTileSize - 4, renderTileSize - 4);
        ctx.strokeStyle = '#FF0000';
        ctx.lineWidth = 2;
        ctx.strokeRect((x - startX) * renderTileSize + 2, (y - startY) * renderTileSize + 2, renderTileSize - 4, renderTileSize - 4);
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('E', (x - startX) * renderTileSize + renderTileSize / 2, (y - startY) * renderTileSize + renderTileSize / 2);
        ctx.fillStyle = '#FFD700';
        ctx.font = 'bold 8px Arial';
        ctx.fillText(`Nv.${enemy.level || '?'}`, (x - startX) * renderTileSize + renderTileSize / 2, (y - startY) * renderTileSize + renderTileSize - 4);
      });

      if (showObjects) objects.forEach(obj => {
        if (obj.coordinate !== 0 && !obj.coordinate) return;
        const x = obj.coordinate % width;
        const y = Math.floor(obj.coordinate / width);
        if (x < startX || x >= endX || y < startY || y >= endY) return;
        const isBuried = obj.is_buried || obj.metadata?.buried;
        const objectPad = editorMode === 'game' ? Math.max(7, Math.round(renderTileSize * 0.2)) : 4;
        const objectSize = Math.max(14, renderTileSize - (objectPad * 2));
        const objectX = (x - startX) * renderTileSize + objectPad;
        const objectY = (y - startY) * renderTileSize + objectPad;
        ctx.fillStyle = isBuried ? 'rgba(139, 69, 19, 0.45)' : 'rgba(0, 100, 0, 0.38)';
        ctx.fillRect(objectX, objectY, objectSize, objectSize);
        ctx.strokeStyle = isBuried ? '#8B4513' : '#228B22';
        ctx.lineWidth = 1;
        ctx.strokeRect(objectX, objectY, objectSize, objectSize);

        const objectImg = obj.image ? getObjectImage(obj.image) : null;
        const canDrawImage = objectImg && objectImg.complete && objectImg.naturalWidth > 0;
        if (canDrawImage) {
          const iconSize = Math.max(12, objectSize - 6);
          ctx.drawImage(
            objectImg,
            objectX + (objectSize - iconSize) / 2,
            objectY + (objectSize - iconSize) / 2,
            iconSize,
            iconSize
          );
        } else {
          ctx.fillStyle = '#FFFFFF';
          ctx.font = 'bold 14px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(isBuried ? 'B' : 'O', objectX + objectSize / 2, objectY + objectSize / 2);
        }
      });

      if (showEvents) events.forEach(ev => {
        if (ev.coordinate !== 0 && !ev.coordinate) return;
        const x = ev.coordinate % width;
        const y = Math.floor(ev.coordinate / width);
        if (x < startX || x >= endX || y < startY || y >= endY) return;
        const left = (x - startX) * renderTileSize;
        const top = (y - startY) * renderTileSize;
        const centerX = left + (renderTileSize / 2);
        const centerY = top + (renderTileSize / 2);
        const outerRadius = Math.max(8, Math.round(renderTileSize * 0.3));
        const innerRadius = Math.max(6, Math.round(renderTileSize * 0.18));
        const ringRadius = Math.max(11, Math.round(renderTileSize * 0.39));

        ctx.save();
        ctx.shadowColor = 'rgba(250, 204, 21, 0.72)';
        ctx.shadowBlur = 16;
        ctx.beginPath();
        ctx.arc(centerX, centerY, ringRadius, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(250, 204, 21, 0.95)';
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(120, 53, 15, 0.92)';
        ctx.fill();
        ctx.strokeStyle = '#FDE68A';
        ctx.lineWidth = 3;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
        ctx.fillStyle = '#FBBF24';
        ctx.fill();

        ctx.fillStyle = '#FFF7D6';
        ctx.font = `bold ${Math.max(14, Math.round(renderTileSize * 0.34))}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('!', centerX, centerY + 1);
        ctx.restore();
      });

      if (showPortals) portals.forEach(portal => {
        if (portal.coordenada_origen !== 0 && !portal.coordenada_origen) return;
        const x = portal.coordenada_origen % width;
        const y = Math.floor(portal.coordenada_origen / width);
        if (x >= startX && x < endX && y >= startY && y < endY) {
          ctx.fillStyle = 'rgba(0, 100, 255, 0.5)';
          ctx.fillRect((x - startX) * renderTileSize + 1, (y - startY) * renderTileSize + 1, renderTileSize - 2, renderTileSize - 2);
          ctx.strokeStyle = '#00BFFF';
          ctx.lineWidth = 3;
          ctx.strokeRect((x - startX) * renderTileSize + 1, (y - startY) * renderTileSize + 1, renderTileSize - 2, renderTileSize - 2);
          const time = Date.now() / 1000;
          const alpha = 0.3 + Math.sin(time * 2) * 0.2;
          ctx.fillStyle = `rgba(0, 191, 255, ${alpha})`;
          ctx.fillRect((x - startX) * renderTileSize + 3, (y - startY) * renderTileSize + 3, renderTileSize - 6, renderTileSize - 6);
          ctx.fillStyle = '#FFFFFF';
          ctx.font = 'bold 16px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('D', (x - startX) * renderTileSize + renderTileSize / 2, (y - startY) * renderTileSize + renderTileSize / 2);
        }

        if ((portal.id_sector_destino === sector?.id || Number(portal.id_sector_destino) === Number(sector?.id))
          && (portal.coordenada_destino || portal.coordenada_destino === 0)) {
          const dx = portal.coordenada_destino % width;
          const dy = Math.floor(portal.coordenada_destino / width);
          if (dx >= startX && dx < endX && dy >= startY && dy < endY) {
            ctx.fillStyle = 'rgba(255, 165, 0, 0.35)';
            ctx.fillRect((dx - startX) * renderTileSize + 2, (dy - startY) * renderTileSize + 2, renderTileSize - 4, renderTileSize - 4);
            ctx.strokeStyle = '#FFA500';
            ctx.lineWidth = 2;
            ctx.strokeRect((dx - startX) * renderTileSize + 2, (dy - startY) * renderTileSize + 2, renderTileSize - 4, renderTileSize - 4);
            ctx.fillStyle = '#FFFFFF';
            ctx.font = 'bold 12px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('D', (dx - startX) * renderTileSize + renderTileSize / 2, (dy - startY) * renderTileSize + renderTileSize / 2);
          }
        }
      });

      if (showBotMarker && testBotCoordinate !== null && testBotCoordinate !== undefined) {
        const bx = testBotCoordinate % width;
        const by = Math.floor(testBotCoordinate / width);
        if (bx >= startX && bx < endX && by >= startY && by < endY) {
          ctx.fillStyle = 'rgba(255, 255, 0, 0.2)';
          ctx.fillRect((bx - startX) * renderTileSize + 2, (by - startY) * renderTileSize + 2, renderTileSize - 4, renderTileSize - 4);
          ctx.strokeStyle = '#FACC15';
          ctx.lineWidth = 2;
          ctx.strokeRect((bx - startX) * renderTileSize + 2, (by - startY) * renderTileSize + 2, renderTileSize - 4, renderTileSize - 4);
          ctx.fillStyle = '#FACC15';
          ctx.font = 'bold 14px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('B', (bx - startX) * renderTileSize + renderTileSize / 2, (by - startY) * renderTileSize + renderTileSize / 2);
        }
      }

      if (Array.isArray(movementPreview) && movementPreview.length > 0) {
        const drawDirectionTriangle = (x, y, direction, color) => {
          const pad = 4;
          const left = (x - startX) * renderTileSize;
          const top = (y - startY) * renderTileSize;
          const right = left + renderTileSize;
          const bottom = top + renderTileSize;
          const midX = left + renderTileSize / 2;
          const midY = top + renderTileSize / 2;

          ctx.beginPath();
          if (direction === 'up') {
            ctx.moveTo(midX, top + pad);
            ctx.lineTo(right - pad, bottom - pad);
            ctx.lineTo(left + pad, bottom - pad);
          } else if (direction === 'down') {
            ctx.moveTo(midX, bottom - pad);
            ctx.lineTo(right - pad, top + pad);
            ctx.lineTo(left + pad, top + pad);
          } else if (direction === 'left') {
            ctx.moveTo(left + pad, midY);
            ctx.lineTo(right - pad, top + pad);
            ctx.lineTo(right - pad, bottom - pad);
          } else if (direction === 'right') {
            ctx.moveTo(right - pad, midY);
            ctx.lineTo(left + pad, top + pad);
            ctx.lineTo(left + pad, bottom - pad);
          } else {
            ctx.moveTo(midX, top + pad);
            ctx.lineTo(right - pad, bottom - pad);
            ctx.lineTo(left + pad, bottom - pad);
          }
          ctx.closePath();
          ctx.fillStyle = color;
          ctx.fill();
          ctx.strokeStyle = color.replace('0.28', '0.95');
          ctx.lineWidth = 2;
          ctx.stroke();
        };
        const drawHighlightBox = (x, y, color, label = '') => {
          const left = (x - startX) * renderTileSize;
          const top = (y - startY) * renderTileSize;
          const size = renderTileSize;

          ctx.fillStyle = color;
          ctx.fillRect(left + 3, top + 3, size - 6, size - 6);
          ctx.strokeStyle = color.replace('0.22', '0.95').replace('0.28', '0.95');
          ctx.lineWidth = 2;
          ctx.strokeRect(left + 3, top + 3, size - 6, size - 6);

          if (label) {
            ctx.fillStyle = '#F8FAFC';
            ctx.font = `bold ${Math.max(12, Math.floor(renderTileSize * 0.36))}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(label, left + size / 2, top + size / 2);
          }
        };

        movementPreview.forEach((step) => {
          if (!step || step.coordinate === null || step.coordinate === undefined) return;
          const x = step.coordinate % width;
          const y = Math.floor(step.coordinate / width);
          if (x < startX || x >= endX || y < startY || y >= endY) return;

          const isValid = Boolean(step.valid);
          const color = isValid ? 'rgba(34, 197, 94, 0.28)' : 'rgba(239, 68, 68, 0.28)';
          if (step.shape === 'box') {
            drawHighlightBox(x, y, isValid ? 'rgba(168, 85, 247, 0.22)' : color, step.label || '');
            return;
          }

          drawDirectionTriangle(x, y, step.direction, color);
        });
      }

      if (editorMode === 'portals' && hoverCoord !== null) {
        const hx = hoverCoord % width;
        const hy = Math.floor(hoverCoord / width);
        if (hx >= startX && hx < endX && hy >= startY && hy < endY) {
          ctx.strokeStyle = '#22D3EE';
          ctx.lineWidth = 2;
          ctx.strokeRect((hx - startX) * renderTileSize + 1, (hy - startY) * renderTileSize + 1, renderTileSize - 2, renderTileSize - 2);
        }
      }

      if (showGrid) {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
        ctx.lineWidth = 1;
        for (let i = 0; i <= viewportWidth; i++) {
          ctx.beginPath();
          ctx.moveTo(i * renderTileSize, 0);
          ctx.lineTo(i * renderTileSize, viewportHeight * renderTileSize);
          ctx.stroke();
        }
        for (let j = 0; j <= viewportHeight; j++) {
          ctx.beginPath();
          ctx.moveTo(0, j * renderTileSize);
          ctx.lineTo(viewportWidth * renderTileSize, j * renderTileSize);
          ctx.stroke();
        }
      }
    } catch (error) {
      console.error('MapCanvas render error:', error);
    }
  };

  useEffect(() => {
    if (tilesetLoaded) {
      renderMap();
      const animationFrame = requestAnimationFrame(() => {
        if (portals.length > 0) renderMap();
      });
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [
    tilesetLoaded,
    tiles,
    sector,
    activeLayer,
    cities,
    enemies,
    objects,
    events,
    portals,
    objectImagesVersion,
    editorMode,
    hoverCoord,
    testBotCoordinate,
    tileset,
    renderTileSize,
    sourceTileSize,
    viewportMode,
    viewportWidth,
    viewportHeight,
    startX,
    startY,
    endX,
    endY,
    showCities,
    showEnemies,
    showObjects,
    showEvents,
    showPortals,
    showBotMarker,
    showGrid,
    movementPreview
  ]);

  const handleCanvasInteraction = (e) => {
    if (!isDrawing && editorMode !== 'terrain' && editorMode !== 'test' && !onClickTile) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / renderTileSize);
    const y = Math.floor((e.clientY - rect.top) / renderTileSize);

    if (x >= 0 && x < (viewportMode ? viewportWidth : width) && y >= 0 && y < (viewportMode ? viewportHeight : height)) {
      const worldX = viewportMode ? x + startX : x;
      const worldY = viewportMode ? y + startY : y;
      const coordinate = worldY * width + worldX;
      if (editorMode === 'terrain') {
        if (selectedTile) onTilePaint(coordinate, activeLayer, selectedTile);
        else onTileErase(coordinate, activeLayer);
      } else if (onClickTile) {
        onClickTile(coordinate, editorMode);
      }
    }
  };

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / renderTileSize);
    const y = Math.floor((e.clientY - rect.top) / renderTileSize);

    if (x >= 0 && x < (viewportMode ? viewportWidth : width) && y >= 0 && y < (viewportMode ? viewportHeight : height)) {
      const worldX = viewportMode ? x + startX : x;
      const worldY = viewportMode ? y + startY : y;
      const coordinate = worldY * width + worldX;
      setHoverCoord(coordinate);
      if (editorMode === 'events' && onHoverEventChange) {
        const hoveredEvent = (events || []).find(ev => Number(ev.coordinate) === Number(coordinate));
        onHoverEventChange(hoveredEvent ? Number(hoveredEvent.id_event || hoveredEvent.id) : null, coordinate);
      }
    } else {
      setHoverCoord(null);
      if (editorMode === 'events' && onHoverEventChange) onHoverEventChange(null, null);
    }

    if (e.buttons === 1) handleCanvasInteraction(e);
  };

  return (
    <div className={`relative rounded-2xl border border-slate-800 bg-[#0a0b1e] ${viewportMode ? 'overflow-hidden' : 'overflow-auto max-h-[70vh] custom-scrollbar'}`}>
      {editorMode === 'portals' && hoverCoord !== null && (
        <div className="absolute top-2 left-2 z-10 px-2 py-1 rounded-md bg-black/70 border border-cyan-400/40 text-cyan-200 text-xs font-bold">
          Coord: {hoverCoord}
        </div>
      )}
      <canvas
        ref={canvasRef}
        width={(viewportMode ? viewportWidth : width) * renderTileSize}
        height={(viewportMode ? viewportHeight : height) * renderTileSize}
        onMouseDown={(e) => { handleCanvasInteraction(e); }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => {
          setHoverCoord(null);
          if (editorMode === 'events' && onHoverEventChange) onHoverEventChange(null, null);
        }}
        className="cursor-crosshair shadow-2xl"
      />
    </div>
  );
};

export default MapCanvas;
